'use client';

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';
import { extractListItems, stripHtml } from '@/lib/homepage-mappers';
import type { PncInitiativeListItem, PncInitiativeModal } from '@/types/people-and-culture';

interface AllInitiativesModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: PncInitiativeModal | undefined;
}

function InitiativeAccordionItem({
  entry,
  index,
  isExpanded,
  onToggle,
}: {
  entry: PncInitiativeListItem;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const panelId = `initiative-panel-${entry.id}`;
  const buttonId = `initiative-button-${entry.id}`;
  const itemLabel = String.fromCharCode(65 + index);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        id={buttonId}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full items-center gap-3 py-4 text-left sm:gap-4"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#ed1c22]/10 text-[11px] font-bold text-[#ed1c22]">
          {itemLabel}
        </span>
        <span className="flex-1 text-sm font-bold text-[#0a0a0a] sm:text-[15px]">
          {entry.heading}
        </span>
        <span
          aria-hidden
          className={`flex h-6 w-6 shrink-0 items-center justify-center text-lg font-light text-gray-400 transition-transform duration-200 ${
            isExpanded ? 'rotate-45' : ''
          }`}
        >
          +
        </span>
      </button>

      {isExpanded ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="pb-5 pl-10 pr-2 sm:pl-11"
        >
          <p className="text-[13px] leading-relaxed text-gray-600">{stripHtml(entry.subHeading)}</p>

          {(entry.highlightItem ?? []).map((group) => (
            <div key={group.id} className="mt-4">
              {group.pointerHeading ? (
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#ed1c22]">
                  {group.pointerHeading}
                </p>
              ) : null}
              <ul className="space-y-2">
                {(group.item ?? []).map((item) => {
                  const subBullets = extractListItems(item.descriptionOptional);
                  return (
                    <li
                      key={item.id}
                      className="flex gap-2.5 text-[13px] leading-relaxed text-gray-600"
                    >
                      <span
                        aria-hidden
                        className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#ed1c22]/60"
                      />
                      <span>
                        {stripHtml(item.mainHighlight)}
                        {subBullets.length > 0 ? (
                          <ul className="mt-1.5 space-y-1.5">
                            {subBullets.map((sub) => (
                              <li key={sub} className="flex gap-2.5">
                                <span
                                  aria-hidden
                                  className="mt-[8px] h-1 w-1 shrink-0 rounded-full bg-gray-300"
                                />
                                <span>{sub}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AllInitiativesModal({
  isOpen,
  onClose,
  content,
}: AllInitiativesModalProps) {
  const [mounted, setMounted] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(
    content?.initiativeList?.[0]?.id ?? null,
  );
  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) setExpandedId(content?.initiativeList?.[0]?.id ?? null);
  }, [content, isOpen]);

  useModalScrollLock(isOpen, handleClose);

  if (!mounted || !isOpen || !content) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="all-initiatives-modal-title"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-white px-5 py-4 sm:px-7">
          <h3
            id="all-initiatives-modal-title"
            className="text-sm font-bold text-[#0a0a0a] sm:text-base"
          >
            {content.modalHeading}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-xs text-gray-500 transition hover:bg-gray-200 hover:text-[#0a0a0a]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7">
          {(content.initiativeList ?? []).map((entry, index) => (
            <InitiativeAccordionItem
              key={entry.id}
              entry={entry}
              index={index}
              isExpanded={expandedId === entry.id}
              onToggle={() => setExpandedId((current) => (current === entry.id ? null : entry.id))}
            />
          ))}
        </div>

        <div className="flex shrink-0 justify-end border-t border-gray-100 bg-white px-5 py-4 sm:px-7">
          <SwapTextButton
            text="Close"
            hoverText="Close"
            showArrow={false}
            aria-label="Close initiatives modal"
            onClick={handleClose}
            className="w-full justify-center !rounded-full !px-6 !py-2 !text-xs sm:w-auto"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
